import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';

export type ToastVariant = 'success' | 'warning' | 'danger' | 'info';

export interface ToastInput {
  title?: ReactNode;
  description?: ReactNode;
  variant?: ToastVariant;
  /** Czas w ms. 0 = nie znika sam. Default 4000. */
  duration?: number;
}

interface ToastItem extends ToastInput {
  id: number;
}

interface ToastContextValue {
  toasts: ToastItem[];
  push: (input: ToastInput) => number;
  dismiss: (id: number) => void;
  success: (description: ReactNode, title?: ReactNode) => number;
  error:   (description: ReactNode, title?: ReactNode) => number;
  warning: (description: ReactNode, title?: ReactNode) => number;
  info:    (description: ReactNode, title?: ReactNode) => number;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Hook dostępu do toastów. Wymaga <ToastProvider> w drzewie.
 *
 *   const toast = useToast();
 *   toast.success('Zapisano');
 *   toast.error('Błąd zapisu', 'Spróbuj ponownie');
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast musi być wewnątrz <ToastProvider>');
  return ctx;
}

let counter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const push = useCallback((input: ToastInput): number => {
    const id = ++counter;
    const duration = input.duration ?? 4000;
    setToasts(prev => [...prev, { id, ...input }]);
    if (duration > 0) {
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
    }
    return id;
  }, []);

  const value = useMemo<ToastContextValue>(() => ({
    toasts, push, dismiss,
    success: (description, title) => push({ variant: 'success', description, title }),
    error:   (description, title) => push({ variant: 'danger',  description, title }),
    warning: (description, title) => push({ variant: 'warning', description, title }),
    info:    (description, title) => push({ variant: 'info',    description, title }),
  }), [toasts, push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

const VARIANT_MAP: Record<ToastVariant, { icon: typeof CheckCircle; color: string; border: string }> = {
  success: { icon: CheckCircle,    color: 'text-success', border: 'border-success-border' },
  danger:  { icon: AlertCircle,    color: 'text-danger',  border: 'border-danger-border' },
  warning: { icon: AlertTriangle,  color: 'text-warning', border: 'border-warning-border' },
  info:    { icon: Info,           color: 'text-info',    border: 'border-info-border' },
};

function ToastViewport({ toasts, onDismiss }: { toasts: ToastItem[]; onDismiss: (id: number) => void }) {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      className="fixed top-4 right-4 z-[150] flex flex-col gap-2 pointer-events-none"
      role="region"
      aria-label="Powiadomienia"
    >
      {toasts.map(t => (
        <ToastItemView key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body,
  );
}

function ToastItemView({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: number) => void }) {
  const [leaving, setLeaving] = useState(false);
  const variant = toast.variant ?? 'info';
  const cfg = VARIANT_MAP[variant];
  const Icon = cfg.icon;

  useEffect(() => {
    if (toast.duration === 0) return;
    const leaveTimer = setTimeout(() => setLeaving(true), (toast.duration ?? 4000) - 200);
    return () => clearTimeout(leaveTimer);
  }, [toast.duration]);

  return (
    <div
      role="alert"
      className={[
        'pointer-events-auto min-w-[280px] max-w-sm rounded-xl bg-surface-raised/95 backdrop-blur-xl border shadow-glass',
        cfg.border,
        leaving ? 'animate-fade-in opacity-0 transition-opacity duration-200' : 'animate-slide-in-right',
      ].join(' ')}
    >
      <div className="flex items-start gap-3 p-3">
        <span className={`shrink-0 inline-flex items-center justify-center w-5 h-5 mt-0.5 ${cfg.color} [&>svg]:w-full [&>svg]:h-full`} aria-hidden="true">
          <Icon />
        </span>
        <div className="flex-1 min-w-0">
          {toast.title && <div className={`text-sm font-semibold ${cfg.color} mb-0.5`}>{toast.title}</div>}
          {toast.description && <div className="text-sm text-text-secondary leading-snug">{toast.description}</div>}
        </div>
        <button
          type="button"
          onClick={() => onDismiss(toast.id)}
          className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded text-text-muted hover:text-text-primary hover:bg-surface-hover/60 transition-colors"
          aria-label="Zamknij"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
